import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import Actions from 'actions/Actions';
import UserActions from 'actions/UserActions';
import BeyondIdentityUtils from 'utils/BeyondIdentity';
import PasskeyService from 'services/PasskeyService';

import FrameworkUtils from 'utils/framework';
import passkeysUtils from 'utils/passkeys';
import { SUCCESS_DISPLAY_TIME_MS } from 'constants/passkeys';
import UI from 'utils/UI';
import userUtils from 'utils/User';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';

import LanguageLocaleUtils from 'utils/LanguageLocale';
import PasskeyOptinModalEN from 'components/GlobalModals/PasskeyOptinModal/locales/PasskeyOptinModal_en_US';
import PasskeyOptinModalFR from 'components/GlobalModals/PasskeyOptinModal/locales/PasskeyOptinModal_fr_CA';

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PasskeyOptinModalEN, PasskeyOptinModalFR);

const localization = createStructuredSelector({
    optinCreateHeading: getTextFromResource(getText, 'optinCreateHeading'),
    subheading: getTextFromResource(getText, 'subheading'),
    warningNotification: getTextFromResource(getText, 'warningNotification'),
    termsAndConditions: getTextFromResource(getText, 'termsAndConditions'),
    termsOfUse: getTextFromResource(getText, 'termsOfUse'),
    termsAndConditionsRest: getTextFromResource(getText, 'termsAndConditionsRest'),
    privacyPolicy: getTextFromResource(getText, 'privacyPolicy'),
    maybeLaterButton: getTextFromResource(getText, 'maybeLaterButton'),
    setupButton: getTextFromResource(getText, 'setupButton'),
    createNewPasskey: getTextFromResource(getText, 'createNewPasskey'),
    cancel: getTextFromResource(getText, 'cancel'),
    passkeyCreated: getTextFromResource(getText, 'passkeyCreated'),
    optinCreateAccountHeading: getTextFromResource(getText, 'optinCreateAccountHeading')
});

const fields = createStructuredSelector({ localization });

const functions = (dispatch, ownProps) => {
    const sendPasskeyMetadata = async ({ bindResult, email, securityToken }) => {
        const deviceName = UI.getBrowserName();
        const firstName = securityToken ? null : userUtils.getProfileFirstName();
        const lastName = securityToken ? null : userUtils.getProfileLastName();

        const payload = securityToken
            ? {
                passkeyId: bindResult?.passkey?.passkeyId,
                identityId: bindResult?.passkey?.identity?.id,
                deviceName,
                securityToken,
                pk: true
            }
            : {
                passkeyId: bindResult?.passkey?.passkeyId,
                identityId: bindResult?.passkey?.identity?.id,
                deviceName,
                email,
                firstName,
                lastName
            };

        try {
            await PasskeyService.createPasskeyMetaData(payload);
        } catch (metaErr) {
            Sephora.logger?.warn('Passkey metadata submission failed', {
                error: metaErr?.message || metaErr,
                passkeyId: bindResult?.passkey?.passkeyId,
                identityId: bindResult?.passkey?.identity?.id,
                deviceName,
                email,
                securityTokenPresent: Boolean(securityToken)
            });
        }
    };

    const loginWithPasskey = async bindResult => {
        // Prevent auto-login if user is already signed in (e.g., modal reopened)
        if (userUtils.isSignedIn()) {
            return;
        }

        const passkeyId = bindResult?.passkey?.passkeyId; // backend id for our APIs
        const passkeyCredentialId = bindResult?.passkey?.id; // BI SDK uses credential id
        const identityId = bindResult?.passkey?.identity?.id;

        if (!passkeyCredentialId || !identityId) {
            Sephora.logger?.warn('Missing passkey credential identifiers for login');

            return;
        }

        const login = ownProps.email || bindResult?.passkey?.identity?.username || '';

        if (!login) {
            // No login identifier available (e.g., reset flow without email); skip auto-login
            return;
        }

        dispatch(Actions.showInterstice(true));

        try {
            await dispatch(
                UserActions.signIn(
                    login,
                    undefined, // no password for passkey auth
                    true, // isKeepSignedIn
                    false, // loginForCheckout
                    () => {}, // success callback not needed here
                    () => {}, // failure handled by surrounding try/catch
                    false, // isOrderConfirmation
                    null, // biAccountInfo
                    false, // isTestNTarget
                    false, // isSignInWithMessaging
                    {}, // analyticsData
                    {
                        isPasskeyLogin: true,
                        passkeyId,
                        passkeyCredentialId,
                        identityId
                    }
                )
            );

            // Ensure Redux user state is hydrated after passkey sign-in
            await dispatch(UserActions.getUserFull(null, null, {}));
        } catch (userSyncError) {
            Sephora.logger?.warn('Passkey login user sync failed', userSyncError);
        } finally {
            dispatch(Actions.showInterstice(false));
        }
    };

    const onDismiss = (maybeLater = false) => {
        if (maybeLater) {
            // Track the "Maybe Later" dismissal with placement-based logic
            passkeysUtils.trackDismissal();

            // ACS-3078 AC2: Check if this is the 3rd skip
            const trackingData = passkeysUtils.getTrackingData();
            const currentCount = trackingData?.count || 0;

            // After trackDismissal, count has been incremented
            // If count is now 3, store flag for next page load
            if (currentCount === 3) {
                // Store event284=3 for next page load
                const anaNextPageData = Storage.local.getItem(LOCAL_STORAGE.ANALYTICS_NEXT_PAGE_DATA) || {};

                anaNextPageData.event284 = 3;
                Storage.local.setItem(LOCAL_STORAGE.ANALYTICS_NEXT_PAGE_DATA, anaNextPageData);
            }
        }

        dispatch(Actions.showPasskeyOptinModal({ isOpen: false }));

        // Complete flow flow after dismissing opt-in
        const successCallback = ownProps.successCallback;

        if (typeof successCallback === 'function') {
            const onDismissed = { dismissed: true };
            successCallback(onDismissed);
        }
    };

    return {
        dispatch,
        onDismiss,
        onSetupPasskey: async () => {
            const { email, credentialBindingLink: providedCredentialBindingLink, securityToken } = ownProps;

            if (!providedCredentialBindingLink && !email) {
                return false;
            }

            try {
                dispatch(Actions.showInterstice(true));
                const bindingResponse = providedCredentialBindingLink ? null : await PasskeyService.createPasskeyBinding(email);
                const credentialBindingLink = providedCredentialBindingLink || bindingResponse?.credential_binding_link;

                if (!credentialBindingLink) {
                    Sephora.logger?.warn('Missing credential_binding_link in response');

                    return false;
                }

                // Defense in depth: ensure passkeys are enabled before attempting to bind
                if (!passkeysUtils.isPasskeyEnabled()) {
                    Sephora.logger?.warn('Attempted to create passkey while feature disabled');

                    return false;
                }

                const bindResult = await BeyondIdentityUtils.bindPasskey(credentialBindingLink);

                // Best-effort: send device/user metadata to backend; log but do not block UX on failure
                await sendPasskeyMetadata({ bindResult, email, securityToken });

                // Best-effort: attempt passkey login; log and continue on failure to avoid breaking creation flow
                try {
                    await loginWithPasskey(bindResult);
                } catch (loginError) {
                    Sephora.logger?.warn('Passkey login (post-create) failed', loginError);
                }

                dispatch(Actions.showPasskeyOptinModal({ isOpen: false }));

                const successCallback = ownProps.successCallback;

                if (typeof successCallback === 'function') {
                    successCallback(bindResult);
                }

                return true;
            } catch (error) {
                dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getText('passkeyErrorTitle'),
                        message: getText('passkeyErrorMessage'),
                        buttonText: getText('passkeyErrorButton'),
                        callback: onDismiss
                    })
                );
                throw error;
            } finally {
                dispatch(Actions.showInterstice(false));
            }
        },
        displayInfoModal: (infoModalOptions = {}) => {
            dispatch(
                Actions.showPasskeysInfoModal({
                    ...infoModalOptions
                })
            );
        },
        onSuccess: (infoModalOptions = {}) => {
            dispatch(Actions.showInfoModal({ ...infoModalOptions, isOpen: true }));
            setTimeout(() => {
                dispatch(Actions.showInfoModal({ isOpen: false }));
            }, SUCCESS_DISPLAY_TIME_MS);
        }
    };
};

const withPasskeyOptinModalProps = wrapHOC(connect(fields, functions));

export {
    fields, functions, withPasskeyOptinModalProps
};
